import { useEffect, useMemo, useState } from "react";
import { MenuList } from "./Menu";
import { canAccessInformationFormFromVisibility, resolveAppRole } from "../../routes/routeAccess";
import { getAppSettingsByAppName } from "../../../shared/api/services/AppSettingsService";
import { getActiveConnectEventQuietly } from "../../../shared/api/services/EventsService";
import {
  ACTIVE_EVENT_ACCESS_UPDATED_EVENT,
  fetchEventVisibilitySnapshot,
  getActiveEventIdFromStorage,
  setCachedEventAdminPanelAccess,
  isCurrentUserEventAdminSession,
  readStoredEventVisibility,
  saveActiveEventSnapshot,
} from "../../../features/events/event-access";
import {
  ACTIVE_EVENT_SELECTION_CHANGED,
  readSelectedActiveEventId,
} from "../../../features/events/active-events";

const HIDDEN_EVENT_MENU_KEYS = new Set([
  //'menu.sessions',
  //'menu.b2bPanel',
  //'menu.participatingCompanies',
  //'menu.visitors',
  //'menu.eventGallery',
]);

const USER_ONLY_B2B_PATHS = new Set([
  'sessions',
  'announcements',
  'appointments',
  'my-appointments',
  'tables',
  'meetings/list',
  'visitors',
  'users',
]);

const ADMIN_ONLY_B2B_PATHS = new Set([
  'b2b-admin-session-create',
  'b2b-admin-sessions',
  'b2b-admin-participants',
  'b2b-admin-matches',
  'b2b-admin-announcements',
  'b2b-admin-appointments',
  'b2b-admin-meetings',
  'b2b-admin-tables',
  'b2b-admin-checkin-survey',
  'b2b-admin-company-create',
  'supplier-day-applications',
  'b2b-admin-live-board',
]);

const WEB_B2B_APP_NAME = 'web_b2b';

const getMenuModuleKeys = (item) => {
  if (Array.isArray(item?.moduleKeys)) {
    return item.moduleKeys
      .map((key) => String(key || '').trim())
      .filter(Boolean);
  }
  if (item?.moduleKey) {
    const key = String(item.moduleKey || '').trim();
    return key ? [key] : [];
  }
  return [];
};

export function useMenuFilter() {
  const [appSettingsByModuleKey, setAppSettingsByModuleKey] = useState<Map<string, boolean> | null>(null);
  const [appSettingsStatus, setAppSettingsStatus] = useState<'idle' | 'ready' | 'error'>('idle');
  const [accessVersion, setAccessVersion] = useState(0);
  const [eventVisibility, setEventVisibility] = useState<{
    canSeeParticipants: boolean;
    canSeeVisitors: boolean;
    hasSnapshot: boolean;
    canAccessAdminPanel?: boolean;
    viewerRoles?: string[];
  }>(() => readStoredEventVisibility());

  const appRole = resolveAppRole();

  useEffect(() => {
    const handleAccessUpdated = () => {
      setAccessVersion((v) => v + 1);
    };
    window.addEventListener(ACTIVE_EVENT_ACCESS_UPDATED_EVENT, handleAccessUpdated);
    window.addEventListener('storage', handleAccessUpdated);
    // Ortak etkinlik secimi degisince menu yetkileri yenilenir.
    window.addEventListener(ACTIVE_EVENT_SELECTION_CHANGED, handleAccessUpdated);
    return () => {
      window.removeEventListener(ACTIVE_EVENT_SELECTION_CHANGED, handleAccessUpdated);
      window.removeEventListener(ACTIVE_EVENT_ACCESS_UPDATED_EVENT, handleAccessUpdated);
      window.removeEventListener('storage', handleAccessUpdated);
    };
  }, []);

  const isEventAdminSession = useMemo(
    () => isCurrentUserEventAdminSession(),
    [accessVersion],
  );

  const viewerRoles = Array.isArray(eventVisibility.viewerRoles) ? eventVisibility.viewerRoles : null;
  const isNewUser = !isEventAdminSession && eventVisibility.hasSnapshot && viewerRoles !== null && viewerRoles.length === 0;

  const canSeeParticipantsMenu = true;
  const canSeeVisitorsMenu = true;

  useEffect(() => {
    let isMounted = true;

    const loadAppSettings = async () => {
      try {
        const response = await getAppSettingsByAppName(WEB_B2B_APP_NAME);
        if (!isMounted) return;
        const nextSettingsByModuleKey = new Map<string, boolean>();
        (response?.data ?? []).forEach((setting) => {
          const moduleKey = String(setting?.moduleKey || '').trim();
          if (moduleKey) {
            nextSettingsByModuleKey.set(moduleKey, Boolean(setting?.isEnabled));
          }
        });
        setAppSettingsByModuleKey(nextSettingsByModuleKey);
        setAppSettingsStatus('ready');
      } catch {
        if (!isMounted) return;
        setAppSettingsByModuleKey(null);
        setAppSettingsStatus('error');
      }
    };

    loadAppSettings();

    const handleRefetch = () => {
      setAccessVersion((v) => v + 1);
      loadAppSettings();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setAccessVersion((v) => v + 1);
        loadAppSettings();
      }
    };

    window.addEventListener('focus', handleRefetch);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      window.removeEventListener('focus', handleRefetch);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadVisibility = async () => {
      let activeEventId = readSelectedActiveEventId() || getActiveEventIdFromStorage();

      if (!activeEventId) {
        try {
          const activeEvent = await getActiveConnectEventQuietly();
          activeEventId = String(
            activeEvent?.id ?? activeEvent?.eventId ?? activeEvent?.activeEventId ?? ''
          ).trim();
        } catch {
          activeEventId = '';
        }
      }

      if (!isMounted) return;

      if (!activeEventId) {
        setEventVisibility({ canSeeParticipants: false, canSeeVisitors: false, hasSnapshot: false });
        return;
      }

      if (isEventAdminSession) {
        setEventVisibility({ canSeeParticipants: true, canSeeVisitors: true, hasSnapshot: true });
        return;
      }

      const storedVisibility = readStoredEventVisibility();
      if (!isMounted) return;
      setEventVisibility(storedVisibility);

      try {
        const snapshot = await fetchEventVisibilitySnapshot(activeEventId);
        if (!isMounted) return;
        setEventVisibility({ ...snapshot, hasSnapshot: true });
        setCachedEventAdminPanelAccess({
          eventId: activeEventId,
          canAccessAdminPanel: snapshot.canAccessAdminPanel,
        });
        saveActiveEventSnapshot({
          activeEventId,
          eventId: activeEventId,
          canSeeParticipants: snapshot.canSeeParticipants,
          canSeeVisitors: snapshot.canSeeVisitors,
          participationStatus: snapshot.status,
          canAccessAdminPanel: snapshot.canAccessAdminPanel,
          viewerRoles: snapshot.viewerRoles,
          event: {
            id: activeEventId,
            canSeeParticipants: snapshot.canSeeParticipants,
            canSeeVisitors: snapshot.canSeeVisitors,
            participationStatus: snapshot.status,
            canAccessAdminPanel: snapshot.canAccessAdminPanel,
            viewerRoles: snapshot.viewerRoles,
          },
        });
      } catch {
        if (!isMounted) return;
        setEventVisibility({ canSeeParticipants: false, canSeeVisitors: false, hasSnapshot: false });
      }
    };

    void loadVisibility();

    return () => {
      isMounted = false;
    };
  }, [accessVersion, isEventAdminSession]);

  const visibleMenuList = useMemo(
    () => {
      const filterMenuTree = (items) =>
        items
          .map((item) => {
            const nextItem = { ...item };
            if (Array.isArray(nextItem.content)) {
              nextItem.content = filterMenuTree(nextItem.content);
            }
            return nextItem;
          })
          .filter((item) => {
            if (HIDDEN_EVENT_MENU_KEYS.has(item.title)) return false;

            if (
              item.title === 'menu.participatingCompanies' ||
              item.to === 'customer' ||
              item.to === 'company-list' ||
              item.title === 'menu.companyListExhibitors' ||
              item.to === 'company-list?filter=exhibitor'
            ) {
              return canSeeParticipantsMenu;
            }

            if (
              item.title === 'menu.companyListVisitors' ||
              item.to === 'company-list?filter=visitor' ||
              item.title === 'menu.visitors' ||
              item.to === 'visitors'
            ) {
              return canSeeVisitorsMenu;
            }

            if (
              appRole === 'user' &&
              (item.title === 'menu.informationForm' ||
                item.to === 'stand-participants-information-form' ||
                item.to === 'stand-participants-information-form-list') &&
              !canAccessInformationFormFromVisibility(eventVisibility)
            ) {
              return false;
            }

            const moduleKeys = getMenuModuleKeys(item);
            if (appSettingsStatus === 'ready' && moduleKeys.length > 0) {
              const isEnabled = moduleKeys.some((moduleKey) => {
                if (appSettingsByModuleKey?.has(moduleKey)) {
                  return appSettingsByModuleKey.get(moduleKey) === true;
                }
                return true;
              });
              if (!isEnabled) return false;
            }

            if (appRole === 'b2b_admin') {
              if (!isEventAdminSession) {
                if (item.title === 'menu.b2bAdminSection' || item.title === 'B2B ADMİN') return false;
                if (item.title === 'menu.b2bPanel' || item.title === 'menu.myMeetings' || item.title === 'B2B Home') return false;
                if (item.to && USER_ONLY_B2B_PATHS.has(item.to)) return false;
                if (item.to && ADMIN_ONLY_B2B_PATHS.has(item.to)) return false;
              }
              if (Array.isArray(item.content) && item.content.length === 0) return false;
              return true;
            }

            if (item.title === 'menu.b2bAdminSection' || item.title === 'B2B ADMİN') return false;
            if (item.to && ADMIN_ONLY_B2B_PATHS.has(item.to)) return false;
            if (Array.isArray(item.content) && item.content.length === 0) return false;
            return true;
          });

      return filterMenuTree(MenuList);
    },
    [appRole, isEventAdminSession, isNewUser, appSettingsStatus, appSettingsByModuleKey, canSeeParticipantsMenu, canSeeVisitorsMenu],
  );

  return { visibleMenuList, isEventAdminSession };
}
